import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

// fields: [{ name, label, type: text|number|select|textarea, options:[{value,label}], required }]
export default function EntityDialog({ open, onOpenChange, title, fields, endpoint, initial, onSaved }) {
  const { formatApiError } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => (base[f.name] = initial?.[f.name] ?? (f.type === "number" ? "" : "")));
      setForm({ ...base, ...(initial || {}) });
    }
  }, [open, initial]); // eslint-disable-line

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && (form[f.name] === "" || form[f.name] == null)) {
        toast.error(`${f.label} is required`); return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number" && payload[f.name] !== "" && payload[f.name] != null) payload[f.name] = Number(payload[f.name]); });
      if (initial?.id) await api.put(`${endpoint}/${initial.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Saved successfully");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="entity-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="mb-1.5 block text-sm">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
              {f.type === "select" ? (
                <Select value={String(form[f.name] ?? "")} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                  <SelectContent>
                    {f.options?.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} />
              ) : (
                <Input data-testid={`field-${f.name}`} type={f.type === "number" ? "number" : "text"}
                  value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="dialog-save" className="bg-blue-600 hover:bg-blue-700">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
